/**
 * Organisation Settings Tab - currency, locale, and language preferences.
 *
 * Reads from / writes to the OrganisationPreferences API endpoint.
 * Mirrors the fox/raccoon "Organisation settings" page layout.
 */

import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { ChevronRight, Loader2, Palette } from 'lucide-react'
import api from '@/admin/lib/api'
import { useOrg } from '@/admin/hooks/use-org'
import { AppearanceDialog } from '@/admin/pages/settings/appearance-dialog'
import { Button } from '@/shared/components/ui/button'
import { Label } from '@/shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'

// ---------------------------------------------------------------------------
// Static data
// ---------------------------------------------------------------------------

import { toastApiError } from '@/shared/lib/api-error'
import { COUNTRIES } from '@/shared/lib/countries'
const CURRENCIES = [
  { code: 'GBP', name: 'British Pound Sterling' },
  { code: 'USD', name: 'US Dollar' },
  { code: 'EUR', name: 'Euro' },
  { code: 'AUD', name: 'Australian Dollar' },
  { code: 'CAD', name: 'Canadian Dollar' },
  { code: 'CHF', name: 'Swiss Franc' },
  { code: 'JPY', name: 'Japanese Yen' },
  { code: 'NZD', name: 'New Zealand Dollar' },
  { code: 'SEK', name: 'Swedish Krona' },
  { code: 'NOK', name: 'Norwegian Krone' },
  { code: 'DKK', name: 'Danish Krone' },
  { code: 'ZAR', name: 'South African Rand' },
  { code: 'AED', name: 'UAE Dirham' },
  { code: 'SGD', name: 'Singapore Dollar' },
  { code: 'HKD', name: 'Hong Kong Dollar' },
  { code: 'INR', name: 'Indian Rupee' },
]

const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'fr', name: 'French' },
  { code: 'de', name: 'German' },
  { code: 'es', name: 'Spanish' },
  { code: 'nl', name: 'Dutch' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'it', name: 'Italian' },
  { code: 'sv', name: 'Swedish' },
  { code: 'no', name: 'Norwegian' },
  { code: 'da', name: 'Danish' },
  { code: 'ja', name: 'Japanese' },
  { code: 'ar', name: 'Arabic' },
]

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Preferences {
  default_currency: string
  available_currencies: string[]
  default_country_code: string
  default_language: string
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function OrgSettingsTab() {
  const { t } = useTranslation()
  const { orgUuid } = useOrg()
  const [prefs, setPrefs] = useState<Preferences | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [appearanceOpen, setAppearanceOpen] = useState(false)

  useEffect(() => {
    if (!orgUuid) return
    api
      .get(`/organisations/${orgUuid}/preferences/`)
      .then((res) => setPrefs(res.data))
      .catch((err) => toastApiError(err, 'Failed to load organisation settings'))
      .finally(() => setLoading(false))
  }, [orgUuid])

  async function handleSave() {
    if (!prefs || !orgUuid) return
    setSaving(true)
    try {
      const res = await api.put(`/organisations/${orgUuid}/preferences/`, prefs)
      setPrefs(res.data)
      toast.success('Organisation settings saved')
    } catch (err) {
      toastApiError(err, 'Failed to save organisation settings');
    } finally {
      setSaving(false)
    }
  }

  const currencyName = useMemo(
    () => (code: string) => CURRENCIES.find((c) => c.code === code)?.name ?? code,
    [],
  )

  function toggleCurrency(code: string) {
    if (!prefs) return
    const current = prefs.available_currencies
    const next = current.includes(code)
      ? current.filter((c) => c !== code)
      : [...current, code]
    // Don't allow removing the default currency
    if (!next.includes(prefs.default_currency)) return
    setPrefs({ ...prefs, available_currencies: next })
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-[var(--ink-3)]" />
      </div>
    )
  }

  if (!prefs) return null

  return (
    <div className="max-w-lg space-y-8">
      <h2 className="clara-heading">Platform settings</h2>

      {/* Available currencies */}
      <div>
        <Label className="clara-label">Available currencies</Label>
        <p className="mb-2 text-[12px] text-[var(--ink-3)]">
          Select which currencies are available for your organisation
        </p>
        <div className="flex flex-wrap gap-1.5">
          {CURRENCIES.map((c) => {
            const isActive = prefs.available_currencies.includes(c.code)
            return (
              <button
                key={c.code}
                type="button"
                onClick={() => toggleCurrency(c.code)}
                className={`cursor-pointer rounded-[4px] border-[0.5px] px-2.5 py-1 text-[12px] transition-colors duration-150 ${
                  isActive
                    ? 'border-[var(--green)] bg-[var(--green-bg)] text-[var(--green)]'
                    : 'border-[var(--rule)] bg-transparent text-[var(--ink-3)] hover:border-[var(--ink-3)]'
                }`}
              >
                {c.name}
                {isActive && (
                  <span className="ml-1 text-[10px]">×</span>
                )}
              </button>
            )
          })}
        </div>
      </div>

      {/* Default currency */}
      <div>
        <Label className="clara-label">Default currency</Label>
        <p className="mb-2 text-[12px] text-[var(--ink-3)]">
          Change your default currency
        </p>
        <Select
          value={prefs.default_currency}
          onValueChange={(v) => setPrefs({ ...prefs, default_currency: v })}
        >
          <SelectTrigger className="h-9 w-full rounded-field">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {prefs.available_currencies.map((code) => (
              <SelectItem key={code} value={code}>
                {currencyName(code)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Default country */}
      <div>
        <Label className="clara-label">Default country</Label>
        <p className="mb-2 text-[12px] text-[var(--ink-3)]">
          Change your default country
        </p>
        <Select
          value={prefs.default_country_code}
          onValueChange={(v) => setPrefs({ ...prefs, default_country_code: v })}
        >
          <SelectTrigger className="h-9 w-full rounded-field">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {COUNTRIES.map((c) => (
              <SelectItem key={c.code} value={c.code}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Language */}
      <div>
        <Label className="clara-label">Language</Label>
        <p className="mb-2 text-[12px] text-[var(--ink-3)]">
          Select your preferred language
        </p>
        <Select
          value={prefs.default_language}
          onValueChange={(v) => setPrefs({ ...prefs, default_language: v })}
        >
          <SelectTrigger className="h-9 w-full rounded-field">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {LANGUAGES.map((l) => (
              <SelectItem key={l.code} value={l.code}>
                {l.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Save */}
      <div className="flex justify-end border-t-[0.5px] border-t-[var(--rule)] pt-5">
        <Button
          data-testid="platform-save-btn"
          onClick={handleSave}
          disabled={saving}
          className="h-auto cursor-pointer rounded-[4px] bg-[var(--green)] px-5 py-2 text-[11px] uppercase tracking-[0.1em] text-[var(--bg)] hover:bg-[var(--green-hover)] disabled:opacity-40"
        >
          {saving ? 'Saving...' : 'Save changes'}
        </Button>
      </div>

      {/* Appearance - the tenant design tokens, edited in their own modal (own
          save), so it sits below the preferences save row.

          No extra `can(...)` gate: the whole /settings tree is already behind
          `settings.view` (admin-tier only, deny-listed for external partners),
          which is the same boundary the endpoint enforces, and every other
          control on this page is ungated for the same reason. Gating on
          `platform.manage` would have hidden it from bespoke admin roles that
          were seeded before that codename existed. */}
      <div className="border-t-[0.5px] border-t-[var(--rule)] pt-8">
        <Label className="clara-label">
          {t('appearance.section_label', 'Appearance')}
        </Label>
        <p className="mb-2 text-[12px] text-[var(--ink-3)]">
          {t(
            'appearance.section_description',
            'Brand colours and fonts used across your admin, consumer, and document surfaces',
          )}
        </p>
        <Button
          variant="outline"
          data-testid="appearance-open-btn"
          onClick={() => setAppearanceOpen(true)}
          className="h-auto w-full cursor-pointer justify-between rounded-[4px] px-3 py-3 text-left"
        >
          <span className="flex min-w-0 items-center gap-2.5">
            <Palette size={15} className="shrink-0 text-[var(--ink-3)]" />
            <span className="flex min-w-0 flex-col">
              <span className="text-[13px] text-[var(--ink)]">
                {t('appearance.edit_button', 'Edit appearance')}
              </span>
              <span className="truncate text-[11px] font-normal text-[var(--ink-3)]">
                {t(
                  'appearance.edit_button_description',
                  'Colours, display and body fonts',
                )}
              </span>
            </span>
          </span>
          <ChevronRight size={15} className="shrink-0 text-[var(--ink-3)]" />
        </Button>
      </div>

      <AppearanceDialog open={appearanceOpen} onOpenChange={setAppearanceOpen} />
    </div>
  )
}
